// hooks/writeGate.ts — a write lock scoped to one surface (1.27.70, owner 2026-09-30).
//
// On the Videos pages, members without two-factor sign-in may watch everything but not
// interact: no comments or replies, reactions, saving, sharing, clipping, following and so on.
// The Videos pages provide this context (features/videos/VideoTwoFactorGate); everywhere else
// it is absent and nothing changes. useRequireAuth consults it, so every write action already
// gated for signed-out visitors is gated here too; the few writes that never called
// requireAuth while signed in (Save, Share, Embed, the post-notification bell, the comment
// composer) check useWriteGate() directly.
import { createContext, useContext, useMemo } from 'react';

export interface WriteGate {
  /** True when the viewer is signed in without confirmed two-factor sign-in. */
  locked: boolean;
  /** Bring up the reminder (the Videos pages' yellow flyout). */
  remind: () => void;
}

export const WriteGateContext = createContext<WriteGate | null>(null);

/**
 * Never locked: reporting is how members flag threats, scams and fraud, and an appeal is due
 * process. Both stay open to every signed-in member on every surface.
 */
export const ALWAYS_ALLOWED = new Set(['report', 'appeal']);

/** `allow(label)` is true when the action may go ahead; otherwise it reminds and returns false. */
export function useWriteGate(): { locked: boolean; allow: (label?: string) => boolean } {
  const gate = useContext(WriteGateContext);
  return useMemo(
    () => ({
      locked: Boolean(gate?.locked),
      allow: (label?: string) => {
        if (!gate?.locked || (label !== undefined && ALWAYS_ALLOWED.has(label))) return true;
        gate.remind();
        return false;
      },
    }),
    [gate],
  );
}
